import { useCallback, useEffect, useRef } from 'react';
import { useAppContext } from '../store/AppContext';
import { channelsApi } from '../api/client';

const POLL_INTERVAL_MS = 3000;

export function useSocket(token: string | null) {
  const { appendMessage } = useAppContext();
  const appendMessageRef = useRef(appendMessage);
  appendMessageRef.current = appendMessage;

  // Holds the stop function for the currently active polling loop.
  const stopPollingRef = useRef<(() => void) | null>(null);

  // Stop polling when token disappears or on unmount.
  useEffect(() => {
    if (!token) {
      stopPollingRef.current?.();
      stopPollingRef.current = null;
    }
    return () => {
      stopPollingRef.current?.();
      stopPollingRef.current = null;
    };
  }, [token]);

  const joinChannel = useCallback(
    (channelId: string) => {
      // Cancel any in-flight poll for a previous channel.
      stopPollingRef.current?.();
      stopPollingRef.current = null;

      if (!token) return;

      let active = true;
      let timerId: number | undefined;
      // The createdAt timestamp of the most-recent message we have seen.
      // Used to detect new arrivals on every subsequent poll.
      let lastSeenCreatedAt = '';

      async function fetchAndDispatch() {
        if (!active) return;

        const res = await channelsApi.messages(channelId);
        if (!active) return;

        if (res.ok) {
          const messages = res.data;

          if (lastSeenCreatedAt === '') {
            // First fetch: seed the cursor from the latest message so we only
            // dispatch messages that arrive *after* joining.
            if (messages.length > 0) {
              lastSeenCreatedAt = messages.reduce((max, m) =>
                m.createdAt > max ? m.createdAt : max,
                '',
              );
            }
          } else {
            // Subsequent fetches: find messages newer than our cursor.
            const newMessages = messages
              .filter((m) => m.createdAt > lastSeenCreatedAt)
              .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1));

            for (const message of newMessages) {
              appendMessageRef.current(message);
            }

            if (newMessages.length > 0) {
              lastSeenCreatedAt = newMessages[newMessages.length - 1].createdAt;
            }
          }
        }

        if (active) {
          timerId = window.setTimeout(fetchAndDispatch, POLL_INTERVAL_MS);
        }
      }

      // Start immediately.
      fetchAndDispatch();

      stopPollingRef.current = () => {
        active = false;
        if (timerId !== undefined) clearTimeout(timerId);
      };
    },
    [token],
  );

  return { joinChannel };
}
